import { motion } from 'framer-motion';
import { Heart, Sparkles } from 'lucide-react';

const floatingHearts = [
  { size: 12, top: '15%', left: '5%', delay: 0, duration: 7 },
  { size: 8, top: '60%', left: '8%', delay: 2, duration: 9 },
  { size: 10, top: '30%', right: '3%', delay: 1, duration: 8 },
  { size: 6, top: '75%', right: '10%', delay: 3, duration: 6 },
  { size: 14, top: '45%', left: '92%', delay: 0.5, duration: 10 },
];

export default function HeroSection() {
  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToPortfolio = () => {
    const el = document.querySelector('#portfolio');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="min-h-screen flex items-center pt-20 md:pt-0 relative overflow-hidden"
      style={{ backgroundColor: 'var(--bg-base)' }}
      data-testid="hero-section"
    >
      {/* Decorative floating pink blobs */}
      <div className="absolute top-20 -left-32 w-96 h-96 blob-pink float-anim opacity-50 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] blob-pink float-anim-reverse opacity-40 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] blob-pink pulse-glow-anim opacity-20 pointer-events-none" />

      {/* Floating hearts */}
      {floatingHearts.map((h, i) => (
        <motion.div
          key={i}
          className="absolute pointer-events-none"
          style={{ top: h.top, left: h.left, right: h.right }}
          animate={{
            y: [0, -30, 10, -20, 0],
            rotate: [0, 10, -10, 5, 0],
            opacity: [0.2, 0.5, 0.3, 0.6, 0.2],
          }}
          transition={{
            duration: h.duration,
            delay: h.delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <Heart size={h.size} style={{ color: 'var(--primary-light)' }} className="fill-current" />
        </motion.div>
      ))}

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Text Block */}
          <div className="md:col-span-7 space-y-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex items-center gap-2"
            >
              <Sparkles size={14} style={{ color: 'var(--primary)' }} />
              <p
                className="font-body text-xs font-semibold uppercase tracking-[0.2em]"
                style={{ color: 'var(--text-accent)' }}
              >
                Wedding / Birthday / Saree Ceremony Invitation Websites
              </p>
            </motion.div>

            <motion.h1
              className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-none"
              style={{ color: 'var(--text-primary)' }}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <motion.span
                className="block"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4 }}
              >
                Crafting Digital
              </motion.span>
              <motion.span
                className="italic block"
                style={{ color: 'var(--primary)' }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.6 }}
              >
                Celebrations
              </motion.span>
            </motion.h1>

            <motion.p
              className="font-body text-base md:text-lg font-normal leading-relaxed max-w-lg"
              style={{ color: 'var(--text-secondary)' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
            >
              I design and develop bespoke invitation websites that turn your
              special moments into unforgettable digital experiences. From
              elegant weddings to joyful birthdays.
            </motion.p>

            <motion.div
              className="flex flex-wrap gap-4 pt-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
            >
              <button
                onClick={scrollToPortfolio}
                className="btn-primary rounded-full"
                data-testid="hero-view-work-btn"
              >
                View My Work
              </button>
              <button
                onClick={scrollToContact}
                className="btn-outline rounded-full"
                data-testid="hero-contact-btn"
              >
                Get In Touch
              </button>
            </motion.div>
          </div>

        </div>
      </div>
    </section>
  );
}
